import React from 'react';
import {
    Alert,
    Button,
    Checkbox,
    Chip,
    FormControlLabel,
    TableSortLabel,
    TextField,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';

import { USER_SORT_COLUMNS } from '../../../common/setupUserFilters.js';
import { lastWelcomeEmail } from '../../../common/welcomeEmailApi.js';

const welcomeDateFormat = new Intl.DateTimeFormat('da-DK', { dateStyle: 'medium', timeStyle: 'short' });

const renderLastWelcomeEmail = (welcomeEmails) => {
    const last = lastWelcomeEmail(welcomeEmails);
    if (!last) return null;
    const when = welcomeDateFormat.format(last.sent_at);

    return (
        <span className={`setup-users-welcome setup-users-welcome-${last.outcome}`}>
            {last.outcome === 'sent' ? `Velkomstmail sendt ${when}` : `Velkomstmail fejlede ${when}`}
        </span>
    );
};

    // The table cells stay readable by showing the first couple of attachments and a
    // "+n" chip for the rest — the full list lives in the editor dialog.
    const renderAttachmentChips = (ids = [], getLabel) => {
        if (ids.length === 0) return <span className="empty-inline">Ingen</span>;

        const shown = ids.slice(0, 2);

        return (
            <div className="setup-users-chips">
                {shown.map((id) => (
                    <Chip key={id} size="small" label={getLabel(id)} className="setup-chip" />
                ))}
                {ids.length > shown.length && (
                    <Chip
                        size="small"
                        label={`+${ids.length - shown.length}`}
                        className="setup-chip"
                    />
                )}
            </div>
        );
    };

const UsersPanel = ({ tab, userOptions, saving }) => {
    const {
        activeUserFilterCount,
        changeUserSort,
        clearUserFilters,
        getShortCompanyLabel,
        getShortWasteReceiverLabel,
        openUserEditor,
        setUserSearch,
        setWelcomeEmailUserId,
        toggleUserFilterValue,
        userFilterGroups,
        userFilterSelections,
        userSearch,
        userSortColumn,
        userSortDirection,
        visibleUsers,
    } = tab;

    return (
        <div className="setup-users-layout">
            <div className="setup-list-panel setup-filter-panel">
                <div className="setup-list-header">
                    <h2>Filtre</h2>
                    <Button
                        size="small"
                        onClick={clearUserFilters}
                        disabled={activeUserFilterCount === 0 && !userSearch}
                    >
                        Ryd
                    </Button>
                </div>

                <div className="setup-filter-search">
                    <TextField
                        label="Søg"
                        placeholder="Navn, modtager eller virksomhed"
                        value={userSearch}
                        onChange={(event) => setUserSearch(event.target.value)}
                        size="small"
                        fullWidth
                    />
                </div>

                <div className="setup-filter-groups">
                    {userFilterGroups.map((group) => (
                        <div className="setup-filter-group" key={group.key}>
                            <h3>{group.label}</h3>
                            {group.options.map((option) => (
                                <FormControlLabel
                                    key={`${group.key}-${option.value}`}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={(userFilterSelections[group.key] || []).includes(
                                                option.value,
                                            )}
                                            onChange={() =>
                                                toggleUserFilterValue(group.key, option.value)
                                            }
                                        />
                                    }
                                    label={option.label}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            <div className="setup-detail-panel setup-users-panel">
                <div className="setup-detail-header">
                    <div>
                        <h2>Brugere</h2>
                        <p>
                            Viser {visibleUsers.length} af {userOptions.length} brugere
                        </p>
                    </div>
                </div>

                {visibleUsers.length === 0 ? (
                    <Alert severity="info">
                        {userOptions.length === 0
                            ? 'Der er endnu ingen brugere.'
                            : 'Ingen brugere matcher de valgte filtre.'}
                    </Alert>
                ) : (
                    <div className="setup-users-table-wrapper">
                        <table className="setup-users-table">
                            <thead>
                                <tr>
                                    {USER_SORT_COLUMNS.map((column) => {
                                        const active = column.key === userSortColumn;

                                        return (
                                            <th
                                                key={column.key}
                                                scope="col"
                                                aria-sort={
                                                    active
                                                        ? (userSortDirection === 'asc'
                                                            ? 'ascending'
                                                            : 'descending')
                                                        : 'none'
                                                }
                                            >
                                                <TableSortLabel
                                                    active={active}
                                                    direction={active ? userSortDirection : 'asc'}
                                                    onClick={() => changeUserSort(column.key)}
                                                >
                                                    {column.label}
                                                </TableSortLabel>
                                            </th>
                                        );
                                    })}
                                    <th scope="col" aria-label="Handlinger" />
                                </tr>
                            </thead>
                            <tbody>
                                {visibleUsers.map((user) => (
                                    <tr key={user.id}>
                                        <td>
                                            <span className="setup-users-name">{user.label}</span>
                                            <span className="setup-users-secondary">{user.email || user.id}</span>
                                            {renderLastWelcomeEmail(user.welcomeEmails)}
                                        </td>
                                        <td>
                                            {renderAttachmentChips(
                                                user.wasteReceivers,
                                                getShortWasteReceiverLabel,
                                            )}
                                        </td>
                                        <td>
                                            {renderAttachmentChips(user.companies, getShortCompanyLabel)}
                                        </td>
                                        <td>
                                            {user.isAdmin ? (
                                                <Chip size="small" label="Admin" className="setup-chip" />
                                            ) : (
                                                <span className="empty-inline">Nej</span>
                                            )}
                                        </td>
                                        <td className="setup-users-row-actions">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<SendIcon />}
                                                onClick={() => setWelcomeEmailUserId(user.id)}
                                                disabled={saving}
                                                title="Send velkomstmail med nyt link"
                                            >
                                                Velkomstmail
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<EditIcon />}
                                                onClick={() => openUserEditor(user)}
                                                disabled={saving}
                                            >
                                                Rediger
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UsersPanel;
